import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { api, connectSse, getJwtExpMs, refreshTokensIfPossible } from '../lib/api'
import { hasAnyPermission, hasAnyRole, Permissions, Roles } from './rbac'

const AuthContext = createContext(undefined)

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null)
    const [initializing, setInitializing] = useState(true)
    const sseRef = useRef(null)
    const refreshTimerRef = useRef(null)

    const clearRefreshTimer = useCallback(() => {
        if (refreshTimerRef.current) {
            try { clearTimeout(refreshTimerRef.current) } catch { }
            refreshTimerRef.current = null
        }
    }, [])

    const scheduleProactiveRefresh = useCallback(() => {
        clearRefreshTimer()
        const token = localStorage.getItem('accessToken')
        const refreshToken = localStorage.getItem('refreshToken')
        if (!token || !refreshToken) return

        // 탭이 숨김 상태(백그라운드/PC 잠금 등)일 때는 proactive refresh를 멈춘다.
        // - 백그라운드 타이머 스로틀링으로 어차피 타이밍이 흔들릴 수 있고,
        // - 잠금 중 불필요한 세션 연장을 줄이기 위함.
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            return
        }

        const expMs = getJwtExpMs(token)
        if (!expMs) return

        // 만료 2분 전 갱신(업무 시간 탭 상시 유지 + 클럭 오차 대비)
        const refreshAt = expMs - 2 * 60 * 1000
        const delay = Math.max(0, refreshAt - Date.now())
        refreshTimerRef.current = setTimeout(async () => {
            await refreshTokensIfPossible()
            // 성공/실패는 refreshTokensIfPossible 내부에서 처리(성공: tokenRefreshed, 실패: logout)
            scheduleProactiveRefresh()
        }, delay)
    }, [clearRefreshTimer])

    // 탭이 다시 활성화되면 즉시 refresh(필요 시) 후 타이머 재스케줄
    useEffect(() => {
        const onVisibilityChange = async () => {
            try {
                if (typeof document === 'undefined') return
                if (document.visibilityState === 'hidden') {
                    clearRefreshTimer()
                    return
                }

                // visible로 돌아오면: 만료/임박 가능성이 높으니 한번 refresh 시도
                const token = localStorage.getItem('accessToken')
                const refreshToken = localStorage.getItem('refreshToken')
                if (!token || !refreshToken) return

                const expMs = getJwtExpMs(token)
                // exp를 못 읽더라도, 401은 인터셉터에서 refresh 처리되므로 여기서는 스케줄만 재설정
                if (expMs && Date.now() >= (expMs - 2 * 60 * 1000)) {
                    await refreshTokensIfPossible()
                }
                scheduleProactiveRefresh()
            } catch {
                // ignore
            }
        }
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', onVisibilityChange)
        }
        return () => {
            try {
                if (typeof document !== 'undefined') {
                    document.removeEventListener('visibilitychange', onVisibilityChange)
                }
            } catch { }
        }
    }, [clearRefreshTimer, scheduleProactiveRefresh])

    const loadUserInfo = async () => {
        try {
            const me = await api.get('/users/me')
            const payload = (me?.data && me.data.data !== undefined) ? me.data.data : me.data
            localStorage.setItem('auth.user', JSON.stringify(payload))
            setUser(payload)
        } catch {
            // 토큰이 없거나 만료된 경우 - 아무것도 하지 않음
        }
    }

    const startSse = useCallback(() => {
        try {
            // 기존 연결이 있으면 정리
            if (sseRef.current) {
                try { sseRef.current.close() } catch { }
                sseRef.current = null
            }
            const es = connectSse(() => localStorage.getItem('accessToken'), (payload) => {
                try {
                    const title = payload?.title || '새 알림이 도착했습니다.'
                    const summary = payload?.summary || payload?.message || ''
                    const msg = summary ? `${title} - ${summary}` : title
                    window?.toast?.show?.(msg, 'info', 5000)
                } catch { }
                try { window.dispatchEvent(new CustomEvent('notifications:new', { detail: payload })) } catch { }
            })
            sseRef.current = es
        } catch {
        }
    }, [])

    useEffect(() => {
        let cancelled = false
        ; (async () => {
                const accessToken = localStorage.getItem('accessToken')
                const refreshToken = localStorage.getItem('refreshToken')
                const userData = localStorage.getItem('auth.user')

                if (accessToken && userData) {
                    try {
                        setUser(JSON.parse(userData))
                    } catch {
                        await loadUserInfo()
                    }
                } else if (refreshToken) {
                    // 리프레시 토큰만 있는 경우 서버에서 복원 시도
                    await loadUserInfo()
                }
                if (!cancelled) setInitializing(false)

                // 로그인/복원된 경우 SSE 연결
                const token = localStorage.getItem('accessToken')
                if (!cancelled && token) {
                    startSse()
                    scheduleProactiveRefresh()
                }
            })()
        return () => {
            cancelled = true
            clearRefreshTimer()
            try {
                if (sseRef.current) {
                    sseRef.current.close()
                    sseRef.current = null
                }
            } catch { }
        }
    }, [startSse, scheduleProactiveRefresh, clearRefreshTimer])

    const login = useCallback(async (email, password) => {
        try {
            // 로그인 요청
            const res = await api.post('/auth/login', { username: email, password: password })
            const { accessToken, refreshToken } = (res?.data && res.data.data) ? res.data.data : res.data
            if (!accessToken || !refreshToken) {
                throw new Error('invalid_tokens')
            }

            // 토큰 저장
            localStorage.setItem('accessToken', accessToken)
            localStorage.setItem('refreshToken', refreshToken)

            // 로그인 성공 후 사용자 정보 가져오기
            const me = await api.get('/users/me')
            const payload = (me?.data && me.data.data !== undefined) ? me.data.data : me.data
            localStorage.setItem('auth.user', JSON.stringify(payload))
            setUser(payload)

            // 로그인 직후 SSE 연결 시작
            startSse()
            scheduleProactiveRefresh()
        } catch (err) {
            // 실패 시 모든 토큰 비우고 에러 전파
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('auth.user')
            setUser(null)
            throw err
        }
    }, [startSse, scheduleProactiveRefresh])

    // 토큰 리프레시 시 SSE를 최신 토큰으로 재연결
    useEffect(() => {
        const onRefreshed = () => {
            try {
                const token = localStorage.getItem('accessToken')
                if (token) startSse()
            } catch { }
            try { scheduleProactiveRefresh() } catch { }
        }
        window.addEventListener('auth:tokenRefreshed', onRefreshed)
        return () => window.removeEventListener('auth:tokenRefreshed', onRefreshed)
    }, [startSse, scheduleProactiveRefresh])

    // 강제 로그아웃 이벤트(리프레시 실패 등) 처리: SSE 종료 + 상태 초기화
    useEffect(() => {
        const onForceLogout = () => {
            clearRefreshTimer()
            try {
                if (sseRef.current) {
                    sseRef.current.close()
                    sseRef.current = null
                }
            } catch { }
            try {
                localStorage.removeItem('accessToken')
                localStorage.removeItem('refreshToken')
                localStorage.removeItem('auth.user')
            } catch { }
            setUser(null)
        }
        window.addEventListener('auth:logout', onForceLogout)
        return () => window.removeEventListener('auth:logout', onForceLogout)
    }, [clearRefreshTimer])

    const logout = useCallback(async () => {
        try {
            await api.post('/auth/logout')
        } catch {
        } finally {
            clearRefreshTimer()
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('auth.user')
            try {
                if (sseRef.current) {
                    sseRef.current.close()
                    sseRef.current = null
                }
            } catch { }
            setUser(null)
        }
    }, [clearRefreshTimer])

    const value = useMemo(() => ({
        user,
        isAuthenticated: !!user,
        initializing,
        login,
        logout,
        hasAnyRole: (roles) => hasAnyRole(user, roles),
        hasAnyPermission: (perms) => hasAnyPermission(user, perms),
        Roles,
        Permissions,
    }), [user, initializing, login, logout])
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
    const ctx = useContext(AuthContext)
    if (!ctx) throw new Error('useAuth must be used within AuthProvider')
    return ctx
}

export function RequireRole({ roles, children, fallback = null }) {
    const { isAuthenticated, user } = useAuth()
    if (!isAuthenticated) return null
    const userRoles = (user && user.roles) || []
    const allowed = roles.some((r) => userRoles.includes(r))
    if (!allowed) return fallback
    return children
}

export function RequirePermission({ permissions, children, fallback = null }) {
    const { isAuthenticated, user } = useAuth()
    if (!isAuthenticated) return null
    const allowed = hasAnyPermission(user, permissions)
    if (!allowed) return fallback
    return children
}
